import { UploadIcon } from "@cynthai/icons";
import type { DropzoneOptions } from "react-dropzone";
import { tv } from "tailwind-variants";

import Button from "./Button";
import { useFileDropzone } from "./hooks/use-file-dropzone";
import type { SlotClassNames } from "./types";

const dropzone = tv({
  slots: {
    root: `flex h-96 w-full cursor-pointer flex-col items-center justify-center
           rounded-md`,
    image: "mb-4 size-18 opacity-50",
  },
  variants: {
    variant: {
      bordered: {
        root: `border-2 border-dashed border-border bg-secondary transition-colors
               hover:border-primary hover:bg-background-2`,
      },
      solid: { root: "relative bg-secondary" },
    },
    isDragActive: { true: {}, false: {} },
    disabled: { true: { root: "cursor-not-allowed opacity-50" }, false: {} },
  },
  compoundVariants: [
    {
      variant: "bordered",
      isDragActive: true,
      class: { root: "border-primary bg-secondary" },
    },
    { variant: "solid", isDragActive: true, class: { root: "opacity-50" } },
  ],
  defaultVariants: { variant: "bordered" },
});

function UploadIllustration({ className }: { readonly className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={72}
      height={72}
      viewBox="0 0 72 72"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M13.5001 23.625H58.4999L72 37.1251H0L13.5001 23.625Z"
        fill="#F5A801"
      />
      <path
        d="M15.7497 28.125H56.2497L67.4996 39.375C67.4996 39.375 66.5773 50.6249 64.9504 50.6249H6.62391C5.26501 50.6249 4.5 39.375 4.5 39.375L15.7497 28.125Z"
        fill="#F5A801"
      />
      <path
        d="M13.5001 3.375H58.5C59.7443 3.375 60.7501 4.38306 60.7501 5.62509V43.8749C60.7501 45.117 59.7443 46.125 58.5 46.125H13.5001C12.2557 46.125 11.25 45.117 11.25 43.8749V5.62485C11.25 4.38306 12.2557 3.375 13.5001 3.375Z"
        fill="#F4F4F4"
      />
      <path
        d="M20.2501 14.6252H51.7499C52.9943 14.6252 54 13.6171 54 12.3751C54 11.1331 52.9943 10.125 51.7499 10.125H20.2501C19.0057 10.125 18 11.1331 18 12.3751C18 13.6171 19.006 14.6252 20.2501 14.6252ZM51.7501 19.1251H20.2501C19.0057 19.1251 18 20.1332 18 21.3752C18 22.6172 19.0057 23.6253 20.2501 23.6253H51.7499C52.9943 23.6253 54 22.6196 54 21.3752C54 20.1308 52.9943 19.1251 51.7501 19.1251ZM51.7501 28.125H20.2501C19.0057 28.125 18 29.1331 18 30.3751C18 31.6171 19.0057 32.6252 20.2501 32.6252H51.7499C52.9943 32.6252 54 31.6171 54 30.3751C54 29.1331 52.9943 28.125 51.7501 28.125Z"
        fill="#C4C4C4"
      />
      <path
        d="M49.4998 37.125V41.6249C49.4998 44.1111 47.4839 46.1249 44.9999 46.1249H26.9999C24.5158 46.1249 22.4999 44.109 22.4999 41.6249V37.125H0V64.1249C0 66.6089 2.01588 68.6248 4.49994 68.6248H67.4998C69.9839 68.6248 71.9998 66.6089 71.9998 64.1249V37.125H49.4998Z"
        fill="#F5A801"
      />
    </svg>
  );
}

interface FileDropzoneProps {
  readonly onFilesAccepted: (files: File[]) => void;
  readonly accept?: DropzoneOptions["accept"];
  readonly maxSize?: number;
  readonly minSize?: number;
  readonly maxFiles?: number;
  readonly disabled?: boolean;
  readonly fileTypeLabel?: string;
  readonly validator?: DropzoneOptions["validator"];

  readonly icon?: React.ReactNode;
  readonly title?: string;
  readonly dragActiveTitle?: string;
  readonly subtitle?: React.ReactNode;
  readonly buttonText?: string;
  readonly supportedFormatsText?: React.ReactNode;
  readonly showButton?: boolean;
  readonly showImage?: boolean;
  readonly overlay?: React.ReactNode;
  readonly children?: (controls: {
    isDragActive: boolean;
    open: () => void;
  }) => React.ReactNode;

  readonly className?: string;
  readonly classNames?: SlotClassNames<ReturnType<typeof dropzone>>;
  readonly variant?: "bordered" | "solid";
}

export function FileDropzone({
  onFilesAccepted,
  accept,
  maxSize,
  minSize,
  maxFiles = 1,
  disabled = false,
  fileTypeLabel,
  validator,
  icon,
  title = "Drag and drop your file here",
  dragActiveTitle,
  subtitle = "or",
  buttonText = "Browse files",
  supportedFormatsText,
  showButton = true,
  showImage = true,
  overlay,
  children,
  className,
  classNames,
  variant = "bordered",
}: FileDropzoneProps) {
  const { getRootProps, getInputProps, isDragActive, open } = useFileDropzone({
    disabled,
    onFilesAccepted,
    fileTypeLabel,
    accept,
    maxSize,
    minSize,
    maxFiles,
    validator,
  });

  const styles = dropzone({ variant, isDragActive, disabled });

  return (
    <div
      {...getRootProps({
        className: styles.root({
          className: `${className ?? ""} ${classNames?.root ?? ""}`.trim(),
        }),
      })}
    >
      {overlay}
      <input {...getInputProps()} />

      {children ? (
        children({ isDragActive, open })
      ) : (
        <>
          {showImage && (
            <UploadIllustration
              className={styles.image({ className: classNames?.image })}
            />
          )}

          {icon}

          <p className="text-text-primary mb-2 text-center text-lg font-medium">
            {isDragActive && dragActiveTitle ? dragActiveTitle : title}
          </p>

          {subtitle && (
            <p className="text-text-secondary mb-4 text-center text-sm">
              {subtitle}
            </p>
          )}

          {showButton && (
            <Button
              variant="bordered"
              color="primary"
              onPress={open}
              isDisabled={disabled}
            >
              <UploadIcon width={16} height={16} />
              {buttonText}
            </Button>
          )}

          {supportedFormatsText && (
            <p className="text-text-tertiary mt-4 max-w-2/5 text-center text-xs">
              {supportedFormatsText}
            </p>
          )}
        </>
      )}
    </div>
  );
}
